import { Theme, ThemeConsumer } from '@rneui/themed';
import React, { RefObject, useEffect, useState } from 'react';
import QRCode from 'react-native-qrcode-svg';
import {
	View, TouchableOpacity, StyleSheet, Platform, ScrollView,
	TextInput
} from 'react-native';
import { ZenIcon, ZenText } from 'zen-core-elements';
import { CustomSearchBar } from '../../customSearchBar';
import { isValidAmount, PAYMENT_METHODS } from '../../../../redux/utils/discountUtils';
import { useAppSelector } from '../../../../redux/hooks';
import CommonService from '../../../../shared/services/commonService';
import { paymentSaveType } from '../../../../redux/types/sellScreenTypes';
import { buildUpiPaymentUrl, getUpiIdFromAsyncStorage } from '../../../../utils/asyncstorageHelper';

const PaymentNotes = ({ onClose, handleSave, paymentMethod, initialValue = 0, notes = "", inputRef }: { onClose: () => void, handleSave: (value: paymentSaveType) => void, paymentMethod: typeof PAYMENT_METHODS[0]["id"], initialValue?: number, notes?: string, inputRef?: RefObject<TextInput | null> }) => {
	const [value, setValue] = useState<null | string>(initialValue ? initialValue?.toString() : null);
	const [reason, setReason] = useState<null | string>(notes ? notes : null);
	const { totalAmount, isSplitPayment, remainingAmount } = useAppSelector((state) => state?.NewSellscreenPaymentSlice)
	const storeName = useAppSelector((state) => state.common?.currentUser?.value?.store?.name)
	const currencySymbol = useAppSelector((state) => state?.common?.currentUser?.value?.purchasedCurrency?.currencySymbol) ?? '₹'
	const [upiId, setUpiId] = useState("")
	const isOnlinePayment = paymentMethod === "Online Payment"

	// Load the store UPI ID configured in Order Settings
	useEffect(() => {
		if (isOnlinePayment) {
			getUpiIdFromAsyncStorage().then(setUpiId).catch((error) => {
				console.log("Get UPI ID error", error)
			})
		}
	}, [isOnlinePayment])

	// Amount encoded in the UPI QR: full total for single payment, entered amount for split payment
	const qrAmount = isSplitPayment ? Number(value) : totalAmount

	const onchangeText = (text: string | null) => {
		if (!text || isValidAmount(text)) {
			if (Number(text) <= totalAmount) {
				setValue(text)
			}
		}
	}

	const disabledSave = () => {
		if (isSplitPayment) {
			if (!value) {
				return true
			}
			return false
		}
		else {
			return false
		}
	}

	return (
		<ThemeConsumer>
			{({ theme }) => {
				const styles = style(theme)
				return (
					<View style={{ flexShrink: 1 }}>
						{isSplitPayment ? null : <View style={styles.header}>
							<ZenText content={paymentMethod} style={styles.headerTitle} />
							<TouchableOpacity onPress={onClose} hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}>
								<ZenIcon name='close' type='antdesign' color={theme?.lightDark} size={24} />
							</TouchableOpacity>
						</View>}

						<ScrollView style={{ flexShrink: 1 }} contentContainerStyle={styles.content} keyboardShouldPersistTaps='handled' showsVerticalScrollIndicator={false}>

							{isSplitPayment ?
								<View style={styles.headerCard}>
									<ZenText content={'Balance'} style={[styles.label, { marginBottom: 0, color: theme?.grey, fontFamily: theme?.bold }]} />
									<ZenText content={CommonService?.currencyStandard(currencySymbol, (remainingAmount - (Number(value) - initialValue)) > 0 ? (remainingAmount - (Number(value) - initialValue))?.toString() : "0")} style={styles.totalAmount} />
								</View>
								: null
							}

							{/* Value Input */}
							{isSplitPayment ? <>
								<ZenText content='Payment Amount' style={styles.label} />

								<View style={{ paddingBottom: 24 }}>
									<CustomSearchBar
										inputRef={inputRef}
										placeholder='Enter a amount'
										search={value}
										setSearch={onchangeText}
										toggleOffset={() => { }}
										backgroundColor={theme?.iceWhite}
										leftIcon={false}
										border={true}
										height={50}
										keyboardType='number-pad'
										leftComponent={<ZenText content={currencySymbol} style={styles.currencyPrefix} />}
									/>
								</View>
							</> : <></>}

							{/* UPI QR code for the customer to scan and pay */}
							{isOnlinePayment ? (
								upiId ? (
									qrAmount > 0 ? (
										<View style={styles.qrContainer}>
											<ZenText content='Scan to pay with any UPI app' style={styles.label} />
											<View style={styles.qrBox}>
												<QRCode
													value={buildUpiPaymentUrl({ upiId, payeeName: storeName, amount: qrAmount, note: storeName ? `Payment to ${storeName}` : undefined })}
													size={180}
													backgroundColor={theme?.light}
												/>
											</View>
											<ZenText content={CommonService?.currencyStandard(currencySymbol, qrAmount.toFixed(2))} style={styles.qrAmount} />
											<ZenText content={upiId} style={styles.qrUpiId} />
										</View>
									) : (
										<ZenText content='Enter an amount to generate the UPI QR code' style={styles.qrHint} />
									)
								) : (
									<ZenText content='Add a UPI ID in Settings > Order Settings to show a payment QR code' style={styles.qrHint} />
								)
							) : null}

							<ZenText content='Payment Notes (Optional)' style={styles.label} />

							<View style={{ paddingBottom: 5 }}>
								<CustomSearchBar
									inputRef={isSplitPayment ? undefined : inputRef}
									placeholder='Type here...'
									search={reason}
									setSearch={(text) => setReason(text)}
									toggleOffset={() => { }}
									backgroundColor={theme?.iceWhite}
									leftIcon={false}
									border={true}
									height={50}
								/>
							</View>
						</ScrollView>

						{/* Footer Actions */}
						<View style={styles.footer}>
							<TouchableOpacity style={[styles.cancelBtn, isSplitPayment && { backgroundColor: theme?.lightBackgroundColor }]} onPress={onClose}>
								<ZenText content={isSplitPayment ? 'Back' : "Cancel"} style={styles.cancelBtnText} />
							</TouchableOpacity>
							<TouchableOpacity
								style={[styles.addBtn, disabledSave() && styles.disabledBtn]}
								disabled={disabledSave()}
								onPress={() => handleSave({
									paidAmount: isSplitPayment ? Number(value) : totalAmount,
									reason: reason?.toString(),
									amount: (isSplitPayment && (remainingAmount - (Number(value) - initialValue)) > 0) ? Number(value) : totalAmount
								})}
							>
								<ZenText content={'Save'} style={[styles.addBtnText, disabledSave() && { color: theme?.disableTextColor }]} />
							</TouchableOpacity>
						</View>
					</View>
				)
			}}
		</ThemeConsumer>
	);
};

const style = (theme: Theme) => StyleSheet.create({

	headerCard: {
		alignItems: 'center',
		marginBottom: 20
	},
	totalAmount: { fontSize: theme?.bigFontSize + 10, fontFamily: theme?.bold, color: theme?.primary },

	// UPI QR
	qrContainer: { alignItems: 'center', paddingBottom: 20 },
	qrBox: { padding: 12, backgroundColor: theme?.light, borderRadius: 12, borderWidth: 1, borderColor: theme?.softSlate },
	qrAmount: { fontSize: theme?.mediumFontSize, fontFamily: theme?.bold, color: theme?.lightDark, marginTop: 10 },
	qrUpiId: { fontSize: theme?.smallFontSize, fontFamily: theme?.medium, color: theme?.grey, marginTop: 2 },
	qrHint: { fontSize: theme?.smallFontSize, fontFamily: theme?.medium, color: theme?.grey, textAlign: 'center', paddingBottom: 20 },

	header: {
		flexDirection: 'row',
		justifyContent: 'space-between',
		alignItems: 'center',
		// paddingHorizontal: 24,
		paddingBottom: 16,
		borderBottomWidth: 1,
		borderBottomColor: theme?.softSlate,
	},
	headerTitle: {
		fontSize: theme?.mediumFontSize,
		fontFamily: theme?.bold,
		color: theme?.lightDark,
	},
	content: {
		paddingVertical: 24
	},
	label: {
		fontSize: theme?.errorFontSize,
		fontFamily: theme?.medium,
		color: theme?.disabledGray500,
		marginBottom: 8,
		textTransform: 'uppercase',
		letterSpacing: 0.5,
	},
	segmentContainer: {
		flexDirection: 'row',
		backgroundColor: theme.softSlate,
		borderRadius: 12,
		padding: 4,
		marginBottom: 24,
	},
	segment: {
		flex: 1,
		flexDirection: 'row',
		alignItems: 'center',
		justifyContent: 'center',
		paddingVertical: 10,
		borderRadius: 8,
		gap: 8,
	},
	activeSegment: {
		backgroundColor: theme?.light,
		...Platform.select({
			ios: { shadowColor: '#000', shadowOffset: { width: 0, height: 2 }, shadowOpacity: 0.05, shadowRadius: 4 },
			android: { elevation: 2 },
		}),
	},
	segmentText: {
		fontSize: theme?.smallHeaderFontSize,
		fontFamily: theme?.medium,
		color: theme?.disabledGray500,
	},
	activeSegmentText: {
		color: theme?.primary,
		fontFamily: theme?.bold,
	},
	inputContainer: {
		flexDirection: 'row',
		alignItems: 'center',
		backgroundColor: theme?.iceWhite,
		borderWidth: 1.5,
		borderColor: theme?.coolGray,
		borderRadius: 16,
		paddingHorizontal: 16,
		height: 50,
	},
	currencyPrefix: {
		fontSize: theme?.mediumFontSize,
		fontFamily: theme?.medium,
		color: theme?.grey,
		marginRight: 5,
	},
	footer: {
		flexDirection: 'row',
		paddingTop: 16,
		gap: 12,
		borderTopWidth: 1,
		borderTopColor: theme.softSlate,
	},
	cancelBtn: {
		flex: 1,
		paddingVertical: 14,
		alignItems: 'center',
		borderRadius: 12,
		backgroundColor: theme?.iceWhite,
	},
	cancelBtnText: {
		color: theme?.disabledGray500,
		fontSize: theme?.smallFontSize,
		fontFamily: theme?.medium,
	},
	addBtn: {
		flex: 2,
		backgroundColor: theme?.primary,
		paddingVertical: 14,
		alignItems: 'center',
		borderRadius: 12,
	},
	disabledBtn: {
		backgroundColor: theme?.disableGray,
	},
	addBtnText: {
		color: theme?.light,
		fontSize: theme?.smallFontSize,
		fontFamily: theme?.medium,
	},
});

export default PaymentNotes;